import get from 'lodash/get';
import update from 'lodash/update';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { IMAGE_COMING_SOON, UNIT_MEASURE } from '~/core/constants';
import useAppliance from '~/core/hooks/useAppliance';
import { ApplianceType } from '~/core/models';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { CutoutId } from '~/core/types/appliances';
import { ApplianceQtyType } from '~/core/types/appliances';
import {
  formatPriceUnitOrFree,
  getCutoutsCountFromShape,
  getProductDescription,
  imgUrl,
} from '~/core/utils';
import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  decreaseCustomCutouts,
  increaseCustomCutouts,
  resetCustomCutoutById,
  selectCustomCutouts,
  setCustomCutouts,
} from '~/redux/slices/cutout';

type DisplayData = {
  title: string;
  image: string;
  price: string;
};

export const useSidePanelCutoutsTab = () => {
  const dispatch = useAppDispatch();
  const { cutouts, isFetchingOptionalItems: isFetching } = useOptionalItems();
  const { registerUserApplianceAction } = useRegisterUserAction();
  const [isOpenOptionRemoveModal, setIsOpenOptionRemoveModal] = useState(false);
  const [cutoutIdToBeRemoved, setCutoutIdToBeRemoved] = useState<
    CutoutId | undefined
  >(undefined);

  const { deleteAppliancesById } = useAppliance();

  const clearCutoutIdToBeRemoved = () => setCutoutIdToBeRemoved(undefined);

  const customCutouts = useAppSelector(selectCustomCutouts).selected;
  const displayDataById = useMemo(
    () =>
      cutouts.reduce<Record<CutoutId, DisplayData>>(
        (z, cutout) =>
          update(z, cutout.productId, () => ({
            title: getProductDescription(cutout),
            image: imgUrl(cutout.imageUrl ?? IMAGE_COMING_SOON, 300),
            price: formatPriceUnitOrFree(
              cutout.price,
              cutout.unitOfMeasure || UNIT_MEASURE.CUTOUT,
              true
            ),
          })),
        {}
      ),
    [cutouts]
  );

  const {
    getters: { drawingShapes },
  } = useDrawingShapeContext();

  const syncCutoutWithShape = useCallback(
    (cutoutId: CutoutId) => {
      const actualOnShapeCount = getCutoutsCountFromShape(
        drawingShapes,
        cutoutId
      );
      const currentOnShapeQty = customCutouts[cutoutId]?.onShape || 0;
      const currentOnCardQty = customCutouts[cutoutId]?.onCard || 0;
      const needsSync = actualOnShapeCount !== currentOnShapeQty;

      if (!needsSync) {
        return null;
      }

      // Only reset onCard to 0 if we're decreasing from a positive onShape count to 0
      // This means cutouts were actually removed from shapes, not that user is adding to cards
      const shouldResetOnCardQuantity =
        actualOnShapeCount === 0 && currentOnCardQty > 0;
      const updatedCutout = {
        onCard: shouldResetOnCardQuantity ? 0 : currentOnCardQty,
        onShape: actualOnShapeCount,
      };

      return { cutoutId, updatedCutout };
    },
    [customCutouts, drawingShapes]
  );

  // Clean up cutouts when shapes are removed
  useEffect(() => {
    const cutoutUpdates = Object.keys(customCutouts)
      .map(Number)
      .filter((cutoutId) => cutouts.find((c) => c.productId === cutoutId)) // Only sync existing cutouts
      .map(syncCutoutWithShape)
      .filter(
        (update): update is NonNullable<typeof update> => update !== null
      ); // Remove nulls with type guard

    if (cutoutUpdates.length > 0) {
      const newCustomCutouts = cutoutUpdates.reduce(
        (acc, update) => ({
          ...acc,
          [update.cutoutId]: {
            ...(acc[update.cutoutId] || { onCard: 0, onShape: 0 }),
            ...update.updatedCutout,
          },
        }),
        { ...customCutouts }
      );

      dispatch(setCustomCutouts({ selected: newCustomCutouts }));
    }
  }, [drawingShapes, customCutouts, cutouts, dispatch, syncCutoutWithShape]);

  const getQty = (id: CutoutId) =>
    Object.entries(customCutouts?.[id] ?? {}).reduce(
      (sum, [qtyType, qty]) => ({
        ...sum,
        [qtyType]: get(sum, qtyType, 0) + qty,
        all: get(sum, 'all', 0) + qty,
      }),
      { onShape: 0, onCard: 0, all: 0 }
    );

  const increaseOption = (cutoutId: CutoutId) => () => {
    dispatch(
      increaseCustomCutouts({ cutoutId, qtyType: ApplianceQtyType.onCard })
    );
    registerUserApplianceAction({ applianceType: ApplianceType.ELECTRICAL_CUTOUT });
  };

  const decreaseOption = (cutoutId: CutoutId) => {
    if (getQty(cutoutId).all < 1) {
      return undefined;
    }

    return () => {
      const { onCard: customCutoutOnCardsQty } = getQty(cutoutId);

      if (customCutoutOnCardsQty > 0) {
        dispatch(
          decreaseCustomCutouts({ cutoutId, qtyType: ApplianceQtyType.onCard })
        );
      } else {
        setIsOpenOptionRemoveModal(true);
      }
      registerUserApplianceAction({ applianceType: ApplianceType.ELECTRICAL_CUTOUT });
    };
  };

  const updateCutoutQuantity = (cutoutId: CutoutId) => (newQty: number) => {
    const { onShape: qtyOfCustomCutoutCardsInCanvas, all: qtyOfAllCutouts } =
      getQty(cutoutId);

    if (newQty === qtyOfAllCutouts) {
      return;
    }

    if (newQty < qtyOfCustomCutoutCardsInCanvas) {
      setIsOpenOptionRemoveModal(true);
    }

    dispatch(
      setCustomCutouts({
        selected: {
          ...customCutouts,
          [cutoutId]: {
            onCard: Math.max(0, newQty - qtyOfCustomCutoutCardsInCanvas),
            onShape: qtyOfCustomCutoutCardsInCanvas,
          },
        },
      })
    );
    registerUserApplianceAction({ applianceType: ApplianceType.ELECTRICAL_CUTOUT });
  };

  const resetCustomCutout = (cutoutIdToRemove: CutoutId) => {
    dispatch(resetCustomCutoutById({ cutoutIdToRemove }));
    deleteAppliancesById(cutoutIdToRemove);
  };

  const handleClick = (cutoutId: CutoutId) => () => {
    const qty = getQty(cutoutId);

    if (qty.all === 0) {
      increaseOption(cutoutId)();
    } else if (qty.onShape > 0) {
      setCutoutIdToBeRemoved(cutoutId);
    } else {
      dispatch(resetCustomCutoutById({ cutoutIdToRemove: cutoutId }));
    }
  };

  const closeRemoveModalWithoutChanges = () =>
    setIsOpenOptionRemoveModal(false);

  return {
    cutouts,
    isFetching,
    modalForOptionRemoval: {
      isOpen: isOpenOptionRemoveModal,
      close: closeRemoveModalWithoutChanges,
    },
    getQty,
    increaseOption,
    decreaseOption,
    updateCutoutQuantity,
    handleClick,
    cutoutIdToBeRemoved,
    setCutoutIdToBeRemoved,
    clearCutoutIdToBeRemoved,
    resetCustomCutout,
    displayDataById,
  };
};
